import { useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { isEqual } from 'lodash';
import { KoobPagination } from '@koob/ui';
import { getTripsBooked } from 'api/trip';
import Spinner from '../Spinner';
import { PER_PAGE_FOR_LIST } from 'constants';

// The library, the DMC templates page and the wizard's template picker all
// list the same endpoint — only the filters and the card differ.
export function buildTripRequestFilters(libraryFilters, overrides) {
  return {
    search: libraryFilters?.search ?? '',
    cities: libraryFilters?.cities?.map(city => city.id) ?? [],
    countries: libraryFilters?.countries?.map(country => country.value) ?? [],
    tripDuration: libraryFilters?.tripDuration ?? 50,
    dmcId: libraryFilters?.dmcId?.value
      ? Number(libraryFilters.dmcId.value)
      : undefined,
    userId: parseInt(libraryFilters?.users?.id) ?? undefined,
    departureLocation: libraryFilters?.departureLocation?.[0]?.id ?? '',
    returnLocation: libraryFilters?.returnLocation?.[0]?.id ?? '',
    guided: libraryFilters?.guided,
    segments: libraryFilters?.segments?.map(segment => segment.id) ?? [],
    themes: libraryFilters?.themes?.map(theme => theme.id) ?? [],
    tags: libraryFilters?.tags?.map(tag => tag.id) ?? [],
    withArchived: libraryFilters?.withArchived ?? false,
    ...overrides,
  };
}

export default function TripList({
  filters,
  perPage: initialPerPage = PER_PAGE_FOR_LIST,
  renderCard,
  renderCount,
  columns = 'md:grid-cols-2 lg:grid-cols-3',
}) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(initialPerPage);
  const [totalPages, setTotalPages] = useState();

  const { data, reload, isLoading } = useAsync({
    promiseFn: getTripsBooked,
    filters: { ...filters, page, perPage },
    watchFn: (props, prevProps) => !isEqual(props.filters, prevProps.filters),
    onResolve: response => setTotalPages(response?.headers?.xTotalPages),
  });

  const trips = data?.data ?? [];
  const totalCount = Number(data?.headers?.xTotalCount || 0);

  // ponytail: filters is rebuilt every render, so compare by value.
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  if (isLoading) {
    return <Spinner noAbsolute />;
  }

  return (
    <>
      {renderCount?.(totalCount)}

      <div className={`grid ${columns} items-start gap-5 content-start`}>
        {trips.map(trip => (
          <div key={trip.id}>{renderCard(trip, reload)}</div>
        ))}
      </div>

      {trips.length > 0 && (
        <div className="mt-6">
          <KoobPagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            perPage={perPage}
            setPerPage={setPerPage}
            totalCount={totalCount}
          />
        </div>
      )}
    </>
  );
}
